import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TaskSimulation from "@/components/TaskSimulation";
import RoboMeshInsights from "@/pages/RoboMeshInsights";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { getRoboMeshWelcome, getRoboMeshStarters, getRoboMeshReasoningLabel, getRoboMeshSuggestedLabel } from "@/utils/robomeshTranslations";
import {
  Send,
  Sparkles,
  AlertTriangle,
  Users,
  Clock,
  PawPrint,
  FileCheck,
  Weight,
  Target,
  Play,
  Loader2,
  Bot,
  User as UserIcon,
  MessageSquare,
  BarChart3,
  Zap
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_CHIPS = [
  { id: "hazardous", label: "Hazardous Materials", icon: AlertTriangle, color: "red" },
  { id: "humans_nearby", label: "Humans Nearby", icon: Users, color: "orange" },
  { id: "time_critical", label: "Time Critical", icon: Clock, color: "yellow" },
  { id: "pets", label: "Pets Present", icon: PawPrint, color: "green" },
  { id: "regulatory", label: "Regulatory Compliance", icon: FileCheck, color: "blue" },
  { id: "heavy_loads", label: "Heavy Loads", icon: Weight, color: "purple" },
  { id: "precision_required", label: "Precision Required", icon: Target, color: "cyan" }
];

const RoboMesh = () => {
  const [input, setInput] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("simple");  // simple or expert
  const [activeTab, setActiveTab] = useState("solve");  // solve, insights, simulate
  const [currentSimulation, setCurrentSimulation] = useState(null);  // Current task being simulated
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const { language } = useLanguage();
  const t = (text) => translate(text, language);




  useEffect(() => {
    // Aggressive auto-focus for RoboMesh input
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.click();
      }
    };
    
    focusInput();
    const timer1 = setTimeout(focusInput, 100);
    const timer2 = setTimeout(focusInput, 500);
    const timer3 = setTimeout(focusInput, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  useEffect(() => {
    // Welcome message with pre-translated content
    const welcome = getRoboMeshWelcome(mode, language);
    const starters = getRoboMeshStarters(mode, language);
    
    setMessages([{
      role: "assistant",
      content: welcome,
      timestamp: new Date().toISOString(),
      model: "system",
      next_steps: starters
    }]);
  }, [mode, language]);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const toggleTag = (tagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const formatResponse = (text) => {
    if (!text) return text;
    return text
      .replace(/(\d+\.)\s+/g, '\n\n$1 ')
      .replace(/\s+•\s+/g, '\n\n• ')
      .replace(/([.!?])\s+(?!•|\d)([A-Z])/g, '$1\n\n$2')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const renderFormattedText = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const handleSolve = async (promptText = null) => {
    const textToSend = promptText || input;
    if (!textToSend.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: textToSend,
      tags: selectedTags,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    let messageAdded = false;
    const msgId = Date.now();
    let stageUpdates = [];
    let modelProposals = [];

    try {
      const res = await fetch(`${API}/robomesh/solve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_prompt: textToSend,
          tags: selectedTags,
          mode: mode,  // simple or expert
          session_id: Date.now().toString()
        })
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim().startsWith('data: ')) {
            try {
              const data = JSON.parse(line.trim().slice(6));

              if (data.type === 'stage') {
                // Show stage progress message
                setMessages(prev => {
                  const lastMsg = prev[prev.length - 1];
                  if (lastMsg && lastMsg.role === 'system' && lastMsg.isStage) {
                    return [...prev.slice(0, -1), {
                      role: 'system',
                      content: data.message,
                      timestamp: new Date().toISOString(),
                      isStage: true
                    }];
                  } else {
                    return [...prev, {
                      role: 'system',
                      content: data.message,
                      timestamp: new Date().toISOString(),
                      isStage: true
                    }];
                  }
                });
              } else if (data.type === 'model_announce') {
                // Model starting
                setMessages(prev => [...prev, {
                  role: 'model',
                  model: data.model,
                  emoji: data.emoji,
                  content: "",
                  timestamp: new Date().toISOString(),
                  isProposal: true,
                  modelId: data.model + data.number
                }]);
              } else if (data.type === 'model_typing') {
                // Model typing proposal word-by-word
                setMessages(prev => prev.map(msg =>
                  msg.modelId === (data.model + '1') || msg.model === data.model && msg.isProposal
                    ? { ...msg, content: data.text }
                    : msg
                ));
              } else if (data.type === 'model_done') {
                // Model completed
                setMessages(prev => prev.map(msg =>
                  msg.model === data.model && msg.isProposal
                    ? { ...msg, content: data.proposal, isComplete: true }
                    : msg
                ));
              } else if (data.type === 'judge_typing') {
                // Judge typing decision
                const lastMsg = messages[messages.length - 1];
                if (lastMsg && lastMsg.isJudge && !lastMsg.isComplete) {
                  setMessages(prev => prev.map((msg, i) =>
                    i === prev.length - 1 ? { ...msg, content: data.text } : msg
                  ));
                } else {
                  setMessages(prev => [...prev, {
                    role: 'system',
                    content: data.text,
                    timestamp: new Date().toISOString(),
                    isJudge: true
                  }]);
                }
              } else if (data.type === 'judge_complete') {
                setMessages(prev => prev.map((msg, i) =>
                  i === prev.length - 1 && msg.isJudge ? { ...msg, isComplete: true } : msg
                ));
              } else if (data.type === 'model_thinking') {
                // Show stage progress message
                stageUpdates.push(data.message);
                setMessages(prev => {
                  const lastMsg = prev[prev.length - 1];
                  if (lastMsg && lastMsg.role === 'system' && lastMsg.isStage) {
                    return [...prev.slice(0, -1), {
                      role: 'system',
                      content: data.message,
                      timestamp: new Date().toISOString(),
                      isStage: true
                    }];
                  } else {
                    return [...prev, {
                      role: 'system',
                      content: data.message,
                      timestamp: new Date().toISOString(),
                      isStage: true
                    }];
                  }
                });
              } else if (data.type === 'model_announce') {
                // Model announces itself
                setMessages(prev => [...prev, {
                  role: 'model',
                  model: data.model,
                  emoji: data.emoji,
                  content: '',
                  timestamp: new Date().toISOString(),
                  isProposal: true,
                  modelId: `${data.model}_${data.number}`
                }]);
              } else if (data.type === 'model_typing') {
                // Model is typing its proposal word-by-word
                setMessages(prev => prev.map(msg =>
                  msg.modelId === `${data.model}_${prev.filter(m => m.role === 'model' && m.isProposal).length}`
                    ? { ...msg, content: data.text }
                    : msg
                ));
              } else if (data.type === 'model_done') {
                // Model finished typing
                setMessages(prev => prev.map(msg =>
                  msg.model === data.model && msg.isProposal && !msg.isDone
                    ? { ...msg, content: data.proposal, isDone: true }
                    : msg
                ));
              } else if (data.type === 'judge_typing') {
                // Judge is typing decision
                setMessages(prev => {
                  const lastMsg = prev[prev.length - 1];
                  if (lastMsg && lastMsg.role === 'system' && lastMsg.isJudge && !lastMsg.isDone) {
                    return [...prev.slice(0, -1), {
                      ...lastMsg,
                      content: `⚖️ **RoboJudge:** ${data.text}`
                    }];
                  } else {
                    return [...prev, {
                      role: 'system',
                      content: `⚖️ **RoboJudge:** ${data.text}`,
                      timestamp: new Date().toISOString(),
                      isJudge: true
                    }];
                  }
                });
              } else if (data.type === 'judge_complete') {
                // Judge finished typing
                setMessages(prev => prev.map(msg =>
                  msg.isJudge && !msg.isDone
                    ? { ...msg, isDone: true }
                    : msg
                ));
              } else if (data.type === 'metrics') {
                // Show metrics
                const metricsMsg = `📊 **Analysis Complete:**\n• Complexity: ${data.complexity}/10\n• Safety Risk: ${data.safety}\n• Models Consulted: ${data.models_used}`;
                setMessages(prev => [...prev, {
                  role: 'system',
                  content: metricsMsg,
                  timestamp: new Date().toISOString(),
                  isMetrics: true
                }]);
              } else if (data.type === 'chunk') {
                fullText = data.full_text;

                if (!messageAdded) {
                  setLoading(false);
                  setMessages(prev => [...prev, {
                    role: "assistant",
                    content: formatResponse(fullText),
                    timestamp: new Date().toISOString(),
                    model: "robomesh",
                    messageId: msgId
                  }]);
                  messageAdded = true;
                } else {
                  setMessages(prev => prev.map(msg =>
                    msg.messageId === msgId
                      ? { ...msg, content: formatResponse(fullText) }
                      : msg
                  ));
                }
              } else if (data.type === 'done') {
                setMessages(prev => prev.map(msg =>
                  msg.messageId === msgId
                    ? {
                        ...msg,
                        content: formatResponse(fullText),
                        run_id: data.run_id,
                        next_steps: data.next_steps || [],
                        simulation_steps: data.simulation_steps || [],
                        isComplete: true
                      }
                    : msg
                ));
                
                // Save simulation for the simulate tab
                if (data.simulation_steps && data.simulation_steps.length > 0) {
                  setCurrentSimulation({
                    task: textToSend,
                    steps: data.simulation_steps
                  });
                }
                
                // Scroll to BEGINNING of response so user can start reading
                setTimeout(() => {
                  const assistantMessages = document.querySelectorAll('[data-role="assistant"]');
                  const lastAssistant = assistantMessages[assistantMessages.length - 1];
                  if (lastAssistant) {
                    lastAssistant.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                  }
                }, 300);
              }
            } catch (e) {
              console.log('Parse error:', e);
            }
          }
        }
      }
    } catch (e) {
      console.error("RoboMesh error:", e);
      toast.error("Failed to solve task");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="robomesh-page">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{translate("RoboMesh", language)}</h1>
              <p className="text-sm text-gray-400">{translate("Universal reasoning engine for real-world action", language)}</p>
            </div>
          </div>
          
          {/* Mode Toggle */}
          <div className="flex gap-2">
            <Button
              onClick={() => setMode("simple")}
              className={mode === "simple" 
                ? "bg-cyan-500 text-black hover:bg-cyan-600" 
                : "bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10"
              }
            >
              {translate("Simple Mode", language)}
            </Button>
            <Button
              onClick={() => setMode("expert")}
              className={mode === "expert" 
                ? "bg-purple-500 text-white hover:bg-purple-600" 
                : "bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10"
              }
            >
              {translate("Expert Mode", language)}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="bg-[#0A0A0A] border border-white/10 mb-4">
          <TabsTrigger value="solve" className="data-[state=active]:bg-purple-500/10 data-[state=active]:text-purple-400">
            <Zap className="w-4 h-4 mr-2" />
            Solve Tasks
          </TabsTrigger>
          <TabsTrigger value="simulate" className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-400">
            <Play className="w-4 h-4 mr-2" />
            Simulate
          </TabsTrigger>
          <TabsTrigger value="insights" className="data-[state=active]:bg-green-500/10 data-[state=active]:text-green-400">
            <BarChart3 className="w-4 h-4 mr-2" />
            Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="solve" className="flex-1 mt-0">
          {/* Main Chat Container (existing code) */}
      <Card className="flex-1 bg-[#0A0A0A] border-white/10 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-6" ref={scrollRef}>
          <div className="space-y-6 max-w-4xl mx-auto">
            {messages.map((message, idx) => {
              // System stage messages (compact badges)
              if (message.role === 'system' && message.isStage) {
                return (
                  <div key={idx} className="flex justify-center my-2">
                    <Badge className="bg-purple-500/10 text-purple-300 border-purple-500/20 px-4 py-2">
                      {message.content}
                    </Badge>
                  </div>
                );
              }
              // Metrics messages
              if (message.role === 'system' && message.isMetrics) {
                return (
                  <div key={idx} className="flex justify-center my-3">
                    <div className="glass border border-cyan-500/30 rounded-xl px-5 py-3 max-w-xl">
                      <p className="text-sm text-gray-200 whitespace-pre-wrap">{renderFormattedText(message.content)}</p>
                    </div>
                  </div>
                );
              }

              // Model proposals (typing word-by-word)
              if (message.role === 'model' && message.isProposal) {
                return (
                  <div key={idx} className="flex gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">{message.emoji}</span>
                    </div>
                    <div className="flex-1">
                      <div className="glass border border-purple-500/20 rounded-lg px-4 py-3">
                        <p className="text-xs font-semibold text-purple-300 mb-1">
                          {message.emoji} {message.model} {message.isComplete ? 'proposes:' : 'is thinking...'}
                        </p>
                        <p className="text-sm text-gray-300">
                          {message.content}
                          {!message.isComplete && <span className="inline-block w-2 h-4 bg-purple-400 ml-1 animate-pulse" />}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }

              // Judge typing message
              if (message.role === 'system' && message.isJudge) {
                return (
                  <div key={idx} className="flex justify-center my-3">
                    <div className="glass border border-purple-500/30 rounded-xl px-5 py-3 max-w-3xl">
                      <p className="text-sm text-purple-200">
                        {message.content}
                        {!message.isComplete && <span className="inline-block w-2 h-4 bg-purple-400 ml-1 animate-pulse" />}
                      </p>
                    </div>
                  </div>
                );
              }

              // Regular user/assistant messages
              return (
              <div
                key={idx}
                data-role={message.role}
                className={`flex gap-4 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' ? 'bg-cyan-500/20' : 'bg-gradient-to-br from-purple-500/20 to-cyan-500/20'
                }`}>
                  {message.role === 'user' ? (
                    <UserIcon className="w-5 h-5 text-cyan-400" />
                  ) : (
                    <Sparkles className="w-5 h-5 text-purple-400" />
                  )}
                </div>

                <div className={`flex-1 max-w-[85%] ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block text-left ${
                    message.role === 'user' ? 'bg-cyan-500/10 border border-cyan-500/20' : 'glass'
                  } rounded-2xl px-5 py-4`}>
                    {message.tags && message.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {message.tags.map(tag => {
                          const chip = TASK_CHIPS.find(c => c.id === tag);
                          return chip ? (
                            <Badge key={tag} className="bg-white/5 text-gray-300 border-white/10 text-xs">
                              {chip.label}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}

                    <p className={`text-[15px] leading-relaxed whitespace-pre-wrap ${
                      message.role === 'user' ? 'text-white' : 'text-gray-200'
                    }`}>
                      {renderFormattedText(message.content)}
                    </p>

                    {/* Next-step suggestions */}
                    {message.role === 'assistant' && message.next_steps && message.next_steps.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-purple-500/10">
                        {/* Simulate button if simulation available */}
                        {message.simulation_steps && message.simulation_steps.length > 0 && (
                          <Button
                            onClick={() => {
                              setCurrentSimulation({
                                task: messages[idx - 1]?.content || "Task",
                                steps: message.simulation_steps
                              });
                              setActiveTab("simulate");
                            }}
                            className="w-full mb-4 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Simulate This Task
                          </Button>
                        )}
                        
                        <p className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-400" />
                          Suggested next steps
                        </p>
                        <div className="flex flex-col gap-3">
                          {message.next_steps.map((step, sIdx) => (
                            <button
                              key={sIdx}
                              onClick={() => handleSolve(step)}
                              className="text-left text-base text-white bg-white/5 hover:bg-purple-500/10 border border-white/10 hover:border-purple-500/30 rounded-xl px-4 py-3.5 transition-all group cursor-pointer"
                            >
                              <span className="flex items-center gap-3">
                                <MessageSquare className="w-4 h-4 text-purple-400 opacity-70 group-hover:opacity-100 flex-shrink-0" />
                                <span className="leading-relaxed">{step}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className={`text-[10px] text-gray-600 mt-1.5 ${message.role === 'user' ? 'text-right' : ''}`}>
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
            })}

            {loading && (
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                </div>
                <div className="glass rounded-2xl px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-gray-400 text-sm">{getRoboMeshReasoningLabel(language)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t border-white/10 p-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Task Chips */}
            <div className="flex flex-wrap gap-2">
              {TASK_CHIPS.map(chip => (
                <button
                  key={chip.id}
                  onClick={() => toggleTag(chip.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedTags.includes(chip.id)
                      ? 'bg-purple-500/20 text-purple-300 border-2 border-purple-500/50'
                      : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <chip.icon className="w-3 h-3 inline mr-1" />
                  {chip.label}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="flex gap-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSolve();
                  }
                }}
                placeholder={mode === "expert" 
                  ? "Describe your robotics challenge..."
                  : "What do you want to accomplish?"
                }
                autoFocus
                className="flex-1 bg-[#0A0A0A] border-2 border-purple-500/20 focus:border-purple-500/50 rounded-xl px-5 py-4 text-white text-lg placeholder-gray-500 focus:outline-none resize-none"
                rows={3}
              />
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => handleSolve()}
                  disabled={!input.trim() || loading}
                  className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white h-auto px-8 py-4 text-lg font-semibold"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Solve
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="border-white/10 hover:bg-white/5 text-white px-4 py-2"
                  onClick={() => toast.info("Simulation feature coming soon")}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Simulate
                </Button>
              </div>
            </div>

            <p className="text-xs text-gray-500 text-center">
              RoboMesh uses AI council (5 models) to reason about physical tasks. Owning a robot is optional.
            </p>
          </div>
        </div>
      </Card>
    </TabsContent>

    <TabsContent value="simulate" className="flex-1 mt-0">
      <div className="h-full overflow-auto">
        {currentSimulation ? (
          <TaskSimulation
            task={currentSimulation.task}
            steps={currentSimulation.steps}
          />
        ) : (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-12 text-center">
              <Play className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Simulation Available</h3>
              <p className="text-gray-400 mb-6">
                Submit a task in the "Solve Tasks" tab first, then click "Simulate This Task" to see an animated execution preview.
              </p>
              <Button
                onClick={() => setActiveTab("solve")}
                className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20"
              >
                Go to Solve Tasks
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </TabsContent>

    <TabsContent value="insights" className="flex-1 mt-0">
      <div className="h-full overflow-auto">
        <RoboMeshInsights />
      </div>
    </TabsContent>
  </Tabs>
    </div>
  );
};

export default RoboMesh;
